import React, { useEffect, useCallback, useState, useMemo } from 'react'
import {
  sendSearchAnalyticsEvent,
  sendSearchedTermEvent,
  sendHeaderSearchEvent,
} from '@utils/analytics'
import { EventValues } from '@sial/common-utils'
import {
  getValidSearch,
  SearchFocusType,
  ProductSearchType,
} from '@utils/searchUtils'
import { searchResultsRoute, useRouter } from '@src/routes'
import { useCurrentUser, SitePreference } from '@src/utils/useCurrentUser'
import { useUserSession } from '@src/utils/useUserSession'
import { isTermlessSearch } from '@utils/termlessSearch'
import {
  CatalogType,
  GroupedSuggestions,
  SearchSuggestionType,
} from '@src/types/graphql-types'
import { useSearchSuggestionsQuery } from '@src/queries/SearchSuggestionsQuery.generated'
import { useRecentOrderItemsQuery } from '@src/queries/RecentOrderItemsQuery.generated'
import { useDebounce } from 'react-use'
import { useProductCatalogFilter } from '@utils/useProductCatalogFilter'
import {
  useRecentSearch,
  RecentSearchResults,
} from '@src/utils/useRecentSearch'
import isCASNumberQuery from '@utils/isCASNumberQuery'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'

export enum StartingIndexes {
  CATEGORY = 'category',
  PRODUCT_NAME = 'product_name',
  PRODUCT_NUMBER = 'product_number',
  CAS_NUMBER = 'cas_number',
  LINKS = 'LINKS',
  RECENT_SEARCHES = 'RECENT_SEARCHES',
  RECENT_PURCHASES = 'RECENT_PURCHASES',
}

export interface SearchOptions {
  item?: string
  type?: ProductSearchType
  href?: string
  facetValue?: string
  searchType?: string
  focusType?: string
  recentSearch?: boolean
  recentOrderItem?: boolean
  searchValue?: string
  dropdownLabel?: string
}

export type HandleSearch = (options: SearchOptions) => void

export interface Suggestions {
  productName: GroupedSuggestions['productName']
  productNumber: GroupedSuggestions['productNumber']
  casNumber: GroupedSuggestions['casNumber']
  category: GroupedSuggestions['relatedCategories']
}

export type GetIndex = (
  type: StartingIndexes,
  index: number,
  showSuggestions: boolean
) => number

// Helper for checking for chinese characters
function isOnlyChineseCharacters(text: string) {
  return text.match(/[\u3400-\u9FBF]/)
}

export const useHeaderAutoSuggest = (
  inputEl: React.RefObject<HTMLInputElement>,
  isOpen: boolean
) => {
  const router = useRouter()
  const { userSession } = useUserSession()
  const { getSitePreference, userIsLoggedIn, isDarmstadtUser } =
    useCurrentUser()
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const { getRecentSearch, setRecentSearch } = useRecentSearch()
  const productCatalogFilter = useProductCatalogFilter()

  const orgId = getSitePreference(SitePreference.CatalogFilter)
  const decode = (item: string) => {
    try {
      return decodeURI(item)
    } catch (e) {
      return item
    }
  }
  const getSearchTerm = () =>
    router?.query?.term &&
    !Array.isArray(router.query.term) &&
    // if we're not on the SRP or, search term input should be blank
    router?.pathname === `/[country]/[language]/search/[searchTerm]` &&
    !isTermlessSearch(router) &&
    router.query.focus !== SearchFocusType.StructureSearch
      ? decode(router.query.term)
      : ''
  const [searchTerm, setSearchTerm] = useState<string>(getSearchTerm)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    searchTerm || ''
  )

  // Default to Minimum Term Length of 3 unless term is Chinese characters
  const minTermLength =
    userSession.language === 'zh' &&
    isOnlyChineseCharacters(debouncedSearchTerm)
      ? 1
      : 3

  /**
   * `searchTerm` should be used for everything in the UI (think input value), so
   * the response stays snappy. the `debouncedSearchTerm` only needs to be used to
   * populate search suggestions, so we're not spamming the API.
   */
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm)
    },
    100,
    [searchTerm]
  )

  /**
   * only updates on route change
   */
  useEffect(() => {
    if (isTermlessSearch(router)) setSearchTerm('')
  }, [setSearchTerm, router.asPath])

  const types = [
    SearchSuggestionType.ProductName,
    SearchSuggestionType.ProductNumber,
    SearchSuggestionType.CasNumber,
    SearchSuggestionType.Category,
  ]

  const { data: autoSuggestData } = useSearchSuggestionsQuery({
    variables: {
      input: {
        term: debouncedSearchTerm,
        types: isChinaUser ? types.concat(SearchSuggestionType.Pinyin) : types,
        filter: productCatalogFilter,
        catalogType: isDarmstadtUser ? CatalogType.B2b : undefined,
        orgId: isDarmstadtUser ? 'DARMSTADT' : undefined,
      },
    },
    skip: !isOpen || debouncedSearchTerm.length < minTermLength,
    ssr: false,
  })

  const { data: recentOrderItemsData } = useRecentOrderItemsQuery({
    variables: {
      input: { page: 1, sortcolumn: 'orderDate', perpage: 4 },
    },
    skip: !userIsLoggedIn,
  })

  // Handles navigation to SRP after user selects a suggestion
  const handleSearch = useCallback<HandleSearch>(
    ({
      item,
      type,
      href,
      facetValue,
      searchType,
      focusType,
      recentSearch,
      searchValue,
      dropdownLabel,
    }) => {
      const actualSearch = (item || searchTerm).trim()
      const casNumberType =
        isCASNumberQuery(actualSearch.toLowerCase()) &&
        ProductSearchType.CasNumber
      const actualType = casNumberType || type || ProductSearchType.Product

      if (actualSearch) {
        setRecentSearch(actualSearch)
      }

      // Send analytics tracking event for suggestions
      if (type) {
        sendSearchAnalyticsEvent(
          {
            searchType,
            href,
            item,
            searchTerm,
            facetValue,
          },
          focusType
        )
      }

      if (recentSearch) {
        sendHeaderSearchEvent(
          {
            action: EventValues.RecentSearches,
            label: item || '',
          },
          {
            searchTerm: searchTerm,
            searchAutoSuggest: 'recent searches',
            searchAutoSuggestTerm: item,
            searchType: focusType,
            searchComponent: 'global search',
            searchErrorMessage: '',
          }
        )
      }

      if (searchValue && dropdownLabel) {
        sendSearchedTermEvent(`${searchValue} - ${dropdownLabel}`, {
          searchTerm: searchValue,
          searchAutoSuggest: undefined,
          searchAutoSuggestTerm: undefined,
          searchType: dropdownLabel,
          searchComponent: 'global search',
          searchErrorMessage: undefined,
        })
      }

      // If href is passed from a static link selection, navigate to that link
      if (href) return router.push(href)

      // If user clicks search icon with no term focus the input
      if (!actualSearch)
        return inputEl && inputEl.current ? inputEl.current.focus() : null

      // Handle Search & Navigate User to SRP
      const queryTerm = getValidSearch({
        term: actualSearch,
        orgId,
        focus: focusType || SearchFocusType.Products,
        type: actualType,
        selectedFacets: facetValue
          ? [`facet_related_category:${facetValue}`]
          : [],
      })

      const searchHref = `${searchResultsRoute.searchResults(
        actualSearch
      )}${queryTerm}`
      setSearchTerm(actualSearch)
      router.push(`/search/[searchTerm]${queryTerm}`, searchHref)
      window.scrollTo(0, 0)
    },
    [searchTerm, setSearchTerm, orgId, router, inputEl]
  )

  const recentSearches: RecentSearchResults = getRecentSearch()

  const recentPurchases =
    recentOrderItemsData?.recentOrderItems.orderItems || []

  const allProductNumberSuggestions =
    autoSuggestData?.getSearchSuggestions?.suggestions.productNumber

  // Header Autosuggest expects grouped suggestions
  const suggestions = useMemo(() => {
    const productName =
      autoSuggestData?.getSearchSuggestions?.suggestions.productName.slice(
        0,
        4
      ) || []

    const pinyin =
      autoSuggestData?.getSearchSuggestions?.suggestions.pinyin.slice(0, 4) ||
      []

    return {
      category:
        autoSuggestData?.getSearchSuggestions?.suggestions.relatedCategories.slice(
          0,
          4
        ) || [],
      productName: productName.concat(pinyin),
      productNumber:
        autoSuggestData?.getSearchSuggestions?.suggestions.productNumber.slice(
          0,
          4
        ) || [],
      casNumber:
        autoSuggestData?.getSearchSuggestions?.suggestions.casNumber.slice(
          0,
          4
        ) || [],
    }
  }, [autoSuggestData])

  // Total count of all suggestions from every category
  const suggestionsCount = useMemo(() => {
    return Object.keys(suggestions).reduce(
      (acc, cur) => acc + suggestions[cur].length,
      0
    )
  }, [suggestions])

  // DownshiftJS autocomplete component requires setting an index for all suggestions.
  // getItemIndex utility retrieves an index based on # of suggestions and category.
  const getItemIndex = (
    type: StartingIndexes,
    index: number,
    showSuggestions: boolean
  ): number => {
    const recentSearchesCount = recentSearches?.length || 0
    const recentPurchasesCount = recentPurchases?.length || 0
    // default-case not needed since TS shouldn't compile if a StartingIndex is missing.
    // eslint-disable-next-line default-case
    switch (type) {
      case StartingIndexes.CATEGORY:
        return index
      case StartingIndexes.PRODUCT_NAME:
        return suggestions.category.length + index
      case StartingIndexes.PRODUCT_NUMBER:
        return (
          suggestions.category.length + suggestions.productName.length + index
        )
      case StartingIndexes.CAS_NUMBER:
        return (
          suggestions.category.length +
          suggestions.productName.length +
          suggestions.productNumber.length +
          index
        )
      case StartingIndexes.LINKS:
        if (!showSuggestions) {
          return recentSearchesCount + recentPurchasesCount
        }
        return (
          suggestions.category.length +
          suggestions.productName.length +
          suggestions.productNumber.length +
          suggestions.casNumber.length +
          index
        )
      case StartingIndexes.RECENT_SEARCHES:
        return index
      case StartingIndexes.RECENT_PURCHASES:
        return recentSearchesCount + index
    }
  }

  return {
    searchTerm,
    setSearchTerm,
    minTermLength,
    handleSearch,
    allProductNumberSuggestions,
    suggestions,
    suggestionsCount,
    recentSearches,
    recentPurchases,
    getItemIndex,
  }
}
